import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Toaster } from 'sonner';
import { SignupScreen } from './components/SignupScreen';
import { WelcomeScreen } from './components/WelcomeScreen';
import { HowToUseScreen } from './components/HowToUseScreen';
import { MainApplication } from './components/MainApplication';
import { CompletionScreen } from './components/CompletionScreen';
import { ConsultationInProgress } from './components/ConsultationInProgress';
import { detectEntryPoint, type UserData } from './utils/entryPoint';
import { useConsultation } from './hooks/useConsultation';
import { SKIP_SIGNUP_FOR_LANGUAGE } from './config/constants';

type Screen = 'signup' | 'welcome' | 'howToUse' | 'inprogress' | 'application' | 'completion';

/**
 * Gets the consultation ID from the root element's data attribute
 * Falls back to 'animal-welfare-2025' if not found
 */
function getConsultationId(): string {
  const rootElement = document.getElementById('consultation-wizard-root');
  return rootElement?.getAttribute('data-consultation-id') || 'animal-welfare-2025';
}

/**
 * Checks if signup should be skipped based on multiple sources:
 * 1. URL parameter: ?skipSignup=true (highest priority)
 * 2. Data attribute: data-skip-signup="true" on root element
 * 3. Environment variable: VITE_SKIP_SIGNUP_FOR_LANGUAGE (comma-separated language codes)
 */
function shouldSkipSignup(currentLanguage: string): boolean {
  // Priority 1: Check URL parameter
  const params = new URLSearchParams(window.location.search);
  if (params.get('skipSignup') === 'true') {
    console.log('[App] Skipping signup via URL parameter');
    return true;
  }

  // Priority 2: Check data attribute on root element
  const rootElement = document.getElementById('consultation-wizard-root');
  if (rootElement?.getAttribute('data-skip-signup') === 'true') {
    console.log('[App] Skipping signup via data-skip-signup attribute');
    return true;
  }

  // Priority 3: Check environment variable (comma-separated language list)
  if (SKIP_SIGNUP_FOR_LANGUAGE) {
    const skipLanguages = SKIP_SIGNUP_FOR_LANGUAGE.split(',').map((lang: string) => lang.trim().toLowerCase());
    if (skipLanguages.includes(currentLanguage.toLowerCase())) {
      console.log(`[App] Skipping signup for language: ${currentLanguage}`);
      return true;
    }
  }

  return false;
}

export default function App() {
  const { t, ready, i18n } = useTranslation('app');
  const [currentScreen, setCurrentScreen] = useState<Screen | null>(null);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const consultationId = getConsultationId();
  // Use i18n's detected language for consultation translations
  // This ensures UI and consultation content use the same language
  const language = i18n.language || 'en';

  // Wait for i18n to initialize and 'app' namespace to load before detecting entry point
  useEffect(() => {
    const initializeApp = () => {
      const entry = detectEntryPoint();
      setCurrentScreen(entry.screen);
      if (entry.userData) {
        setUserData(entry.userData);
      }
      setIsInitializing(false);
    };

    const checkAndInitialize = () => {
      // Check if i18n is initialized AND the 'app' namespace has loaded
      if (i18n.isInitialized && i18n.hasLoadedNamespace('app')) {
        initializeApp();
        return true;
      }
      return false;
    };

    // Check immediately in case it's already ready
    if (checkAndInitialize()) {
      return;
    }

    const handleInitialized = () => {
      // After initialization, all namespaces should be loaded
      // Use a small delay to ensure all loaded events have fired
      setTimeout(() => {
        if (checkAndInitialize()) {
          // Clean up listener once initialized
          i18n.off('initialized', handleInitialized);
        }
      }, 0);
    };

    // Set up listener for initialization
    i18n.on('initialized', handleInitialized);
    
    // Cleanup listener
    return () => {
      i18n.off('initialized', handleInitialized);
    };
  }, [i18n]);

  // Scroll to top when screen changes
  useEffect(() => {
    if (currentScreen !== null) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentScreen]);

  // Load consultation data
  const { config, translations, loading, error } = useConsultation(
    consultationId,
    language
  );

  const handleSignupComplete = (data: UserData) => {
    setUserData(data);
    // Flow: welcome → signup → howToUse → application
    setCurrentScreen('howToUse');
  };

  const handleHowToUseContinue = () => {
    // Navigate from HowToUseScreen to application
    setCurrentScreen('application');
  };

  const handleWelcomeStart = () => {
    const currentLang = i18n.language || 'en';
    
    if (shouldSkipSignup(currentLang)) {
      // Create minimal userData when signup is skipped
      // This allows the application screen to render without signup data
      const minimalUserData: UserData = {
        firstName: '',
        lastName: '',
        email: '',
        country: '',
        marketingConsent: false,
      };
      setUserData(minimalUserData);
      // Skip signup and go directly to howToUse
      setCurrentScreen('howToUse');
      return;
    }
    
    // Navigate from WelcomeScreen to SignupScreen
    setCurrentScreen('signup');
  };

  const handleApplicationComplete = async () => {
    // Navigate to completion screen
    setCurrentScreen('completion');
  };

  const handleAbandonConsultation = () => {
    // User wants to cancel - go back to welcome
    console.log('[App] User abandoned consultation');
    setCurrentScreen('welcome');
  };

  // Show loading screen while i18n initializes
  if (isInitializing || !currentScreen || !ready) {
    return (
      <div className="flex items-center justify-center h-screen bg-white">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          {ready ? (
            <p className="text-muted-foreground">{t('loading.initializing')}</p>
          ) : (
            <p className="text-muted-foreground">Initializing...</p>
          )}
        </div>
      </div>
    );
  }

  // Handle loading/error states
  if (currentScreen === 'application' && loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p>{t('loading.consultation')}</p>
        </div>
      </div>
    );
  }

  if (currentScreen === 'application' && error) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center max-w-md p-8">
          <h1 className="text-2xl font-bold mb-4 text-destructive">{t('error.title')}</h1>
          <p className="mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-primary text-white px-6 py-3 rounded"
          >
            {t('error.retryButton')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="size-full">
      <Toaster />
      {currentScreen === 'signup' && (
        <SignupScreen onComplete={handleSignupComplete} config={config} />
      )}

      {currentScreen === 'welcome' && config && (
        <WelcomeScreen
          euFormUrl={config.euFormUrl}
          onStart={handleWelcomeStart}
          userData={userData ? {
            email: userData.email,
            country: userData.country,
            firstName: userData.firstName,
            lastName: userData.lastName
          } : undefined}
        />
      )}

      {currentScreen === 'howToUse' && (
        <HowToUseScreen onContinue={handleHowToUseContinue} />
      )}

      {currentScreen === 'inprogress' && userData && config && (
        <ConsultationInProgress
          guideBaseUrl={window.location.origin + window.location.pathname}
          euFormUrl={config.euFormUrl}
          userData={{
            email: userData.email,
            country: userData.country,
            firstName: userData.firstName,
            lastName: userData.lastName
          }}
          onAbandon={handleAbandonConsultation}
        />
      )}

      {currentScreen === 'application' && config && translations && userData && (
        <MainApplication
          config={config}
          translations={translations}
          onComplete={handleApplicationComplete}
          userCountry={userData.country}
          onNavigateToHowToUse={() => setCurrentScreen('howToUse')}
        />
      )}

      {currentScreen === 'completion' && (
        <CompletionScreen />
      )}
    </div>
  );
}
